import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useCallback, useState } from 'react';
import { Banner, ChangeTypes, CreateBannerDTO, UpdateBannerDTO } from '../admin-banners.type';
import { getChanges } from '../utils';
import { createBanner, deleteBanner, updateBanner } from '../actions';

export const useBanners = (initialBanners: Banner[], serviceUrl: string) => {
    const [savedBanners, setSavedBanners] = useState<Banner[]>(initialBanners);
    const [banners, setBanners] = useState<Banner[]>(initialBanners);

    const { displayedBanners, inactiveBanners } = banners.reduce(
        (acc, element) => {
            if (element.displayed) {
                acc.displayedBanners.push(element);
            } else {
                acc.inactiveBanners.push(element);
            }
            return acc;
        },
        {
            displayedBanners: [],
            inactiveBanners: [],
        }
    );

    const handleCreate = useCallback((bannerDTO: CreateBannerDTO) => {
        const id = `new-${bannerDTO.title_en}`;
        setBanners((prev) => [...prev, { ...bannerDTO, id, displayed: false, order: 0 }]);
    }, []);

    const handleUpdate = useCallback((bannerDTO: UpdateBannerDTO) => {
        setBanners((prev) =>
            prev.map((banner) =>
                banner.id === bannerDTO.id ? { ...banner, ...bannerDTO } : banner
            )
        );
    }, []);

    const handleDelete = (id: string) => {
        setBanners((prev) => prev.filter((banner) => banner.id !== id));
    };

    const toggleDisplayed = (id: string, displayed: boolean) => {
        setBanners((prev) =>
            prev.map(({ order, ...banner }) =>
                banner.id === id ? { ...banner, displayed } : banner
            )
        );
    };

    const handleCancelChanges = () => {
        setBanners(savedBanners);
    };

    const onDragEnd = ({ active, over }: DragEndEvent) => {
        if (active.id !== over?.id) {
            setBanners((prev) => {
                const activeIndex = prev.findIndex((item) => item.id === active.id);
                const overIndex = prev.findIndex((item) => item.id === over?.id);
                return arrayMove(prev, activeIndex, overIndex);
            });
        }
    };

    const handleSaveChanges = async () => {
        const changes = getChanges(savedBanners, displayedBanners, inactiveBanners);
        const res = await Promise.all(
            changes.map(({ type, banner }) => {
                switch (type) {
                    case ChangeTypes.CREATE:
                        return createBanner(serviceUrl, banner);
                    case ChangeTypes.DELETE:
                        return deleteBanner(serviceUrl, banner.id);
                    case ChangeTypes.UPDATE:
                        return updateBanner(serviceUrl, banner);
                }
            })
        );
        const updated = banners.reduce((acc, banner) => {
            let index: number | null = null;
            for (let i = 0; i < changes.length; i++) {
                const change = changes[i];
                if (change.banner.id === banner.id) {
                    index = i;
                    break;
                }
            }
            if (index !== null) {
                const changed = res[index];
                if (banner) {
                    acc.push(changed);
                }
            } else {
                acc.push(banner);
            }
            return acc;
        }, []);

        setBanners(updated);
        setSavedBanners(updated);
    };

    return {
        displayedBanners,
        inactiveBanners,
        handleCreate,
        handleUpdate,
        handleDelete,
        toggleDisplayed,
        onDragEnd,
        handleCancelChanges,
        handleSaveChanges,
    };
};
